import React, { useMemo, useState } from 'react';
import { Input, Modal, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { pickPageName } from '../../../../utils/dataPortalCache';
import { DataPortalGroupType } from '../graphql/queries';

interface Props {
    open: boolean;
    /** 单个移动时展示看板名；批量移动时传选中条数 */
    subject: string;
    groups: DataPortalGroupType[];
    /** 当前所属分组，用于高亮；批量移动时传 undefined */
    currentGroupId?: string | null;
    /**
     * 待移动看板所属的门户。分组绑定唯一门户，只有同门户的分组能选。
     *
     * 批量移动且选中看板**跨了多个门户**时传 null：此时无法给出任何合法目标分组
     * （任选一个都会让另一部分看板跨门户），直接提示并只留「退回未分组」。
     * 后端仍会校验（`DATA_PORTAL_PAGE_CROSS_PORTAL_MOVE`），这里是前端先挡一道。
     */
    portalCode: string | null;
    onCancel: () => void;
    /** 选中即生效；null 表示退回未分组 */
    onSelect: (groupId: string | null) => void;
}

/** 「未分组」是虚拟分组，选它等于把 group_id 置空 */
const UNGROUPED_ID = '__ungrouped__';

const MoveToGroupModal: React.FC<Props> = ({
    open,
    subject,
    groups,
    currentGroupId,
    portalCode,
    onCancel,
    onSelect,
}) => {
    const { t, i18n } = useTranslation();
    const [keyword, setKeyword] = useState('');

    const options = useMemo(() => {
        // portalCode 为 null（选中看板跨门户）时一个分组都不列：任选一个都会让另一部分看板跨门户。
        // 只留「退回未分组」——它不涉及分组，任何门户的看板都能退回自己门户的未分组段
        const candidates = portalCode == null ? [] : groups.filter((g) => g.portalCode === portalCode);
        const all = [
            ...candidates.map((group) => ({
                id: group.id,
                name: pickPageName(group, i18n.language),
                count: group.enabledPageCount,
            })),
            { id: UNGROUPED_ID, name: t('pages.dataPortal.ungrouped') as string, count: undefined },
        ];
        const query = keyword.trim().toLowerCase();
        return query ? all.filter((item) => item.name.toLowerCase().includes(query)) : all;
    }, [groups, keyword, i18n.language, t, portalCode]);

    return (
        <Modal
            open={open}
            title={t('central.dataPortalConfig.actions.moveToGroup')}
            onCancel={onCancel}
            footer={null}
            destroyOnClose
        >
            <div className="mb-3 text-sm text-gray-500">{subject}</div>
            {portalCode == null && (
                <div className="mb-3 text-sm text-orange-600">
                    {t('central.dataPortalConfig.crossPortalMoveTip')}
                </div>
            )}
            <Input
                allowClear
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder={t('central.dataPortalConfig.placeholders.searchGroup') as string}
                className="mb-3"
            />
            <div className="max-h-72 overflow-y-auto">
                {options.map((item) => {
                    const isCurrent =
                        currentGroupId === undefined
                            ? false
                            : (currentGroupId ?? UNGROUPED_ID) === item.id;
                    return (
                        <div
                            key={item.id}
                            className={`flex items-center justify-between px-3 py-2 rounded cursor-pointer ${
                                isCurrent ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50'
                            }`}
                            onClick={() => onSelect(item.id === UNGROUPED_ID ? null : item.id)}
                        >
                            <span className="truncate">{item.name}</span>
                            <span className="flex items-center gap-2 flex-none">
                                {item.count != null && <span className="text-xs text-gray-400">{item.count}</span>}
                                {isCurrent && <Tag color="blue">{t('central.dataPortalConfig.currentGroup')}</Tag>}
                            </span>
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
};

export default MoveToGroupModal;
